/// <reference path="../../css.d.ts" />
"use client";

// @ubm/ui/components/lightbox-gallery — VIEWER MÉDIA COMMUN (interne, lot U9, 05/10/2026). Implémentation MAISON d'après les
// documentations et aperçus PUBLICS de Lightbox gallery, Product gallery et Photo grid (Arc Pro) ; aucun code Pro reçu ni consulté.
//
// Une seule composition média pour les trois galeries : vol depuis la cellule et retour vers la cellule courante, zoom (bouton,
// double clic, Ctrl+molette, pincement, clic-zoom produit), panoramique, balayage entre photos, glisser vers le bas pour fermer.
// Pile, focus, Échap, clic extérieur, verrou de défilement et engagement d'hôte : le moteur Dialog COMMUN (`../dialog/engine`).
// Aucun écouteur document, aucun piège ni positionneur propres : les gestes vivent sur la scène (écouteurs d'élément).
import { forwardRef, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { AnimatePresence, animate, motion, useMotionValue, usePresence, useReducedMotionConfig as useReducedMotion, useTransform } from "motion/react";
import type { MotionValue } from "motion/react";
import { ChevronLeft, ChevronRight, Heart, X, ZoomIn, ZoomOut, ArrowLeft } from "lucide-react";
import { motionTokens } from "../../lib/motion-tokens";
import { useOverlayHostEngaged } from "../../overlays/OverlayHost";
import { Root, Portal, Overlay, Content, Title } from "../dialog/engine";
import { Button } from "../button/button";
import "../../styles/foundation.css";
import styles from "./media-viewer.module.css";

export interface ViewerItem {
  key: string;
  src: string;
  srcSet?: string;
  alt: string;
  width: number;
  height: number;
  title?: string;
  caption?: string;
}

/** Libellés du viewer (extension maison localisable ; défauts anglais = textes publics). */
export interface ViewerLabels {
  close: string;
  /** Bouton fermer du viewer produit (texte public « Close »). */
  closeShort: string;
  /** Nom du viewer produit : « <label>, photo 1 of 4 ». */
  photoOf: (index: number, count: number) => string;
  previous: string;
  next: string;
  zoomIn: string;
  zoomOut: string;
  counter: (index: number, count: number) => string;
  show: (title: string) => string;
  hint: string;
  back: string;
  favorite: string;
  unfavorite: string;
  filmstrip: string;
}
export const viewerDefaultLabels: ViewerLabels = {
  close: "Close viewer",
  closeShort: "Close",
  photoOf: (i, n) => `photo ${i + 1} of ${n}`,
  previous: "Previous photo",
  next: "Next photo",
  zoomIn: "Zoom in",
  zoomOut: "Zoom out",
  counter: (i, n) => `${i + 1} of ${n}`,
  show: (title) => `Show ${title}`,
  hint: "Click the photo to zoom",
  back: "Back to all photos",
  favorite: "Favorite",
  unfavorite: "Remove from favorites",
  filmstrip: "Photos",
};

export type ViewerChrome = "lightbox" | "product" | "library";

export interface MediaViewerProps {
  open: boolean;
  /** Demande de fermeture (Échap, bouton, glisser vers le bas, voile). Le parent remet `open` à false. */
  onClose: () => void;
  items: ViewerItem[];
  index: number;
  onIndexChange: (index: number) => void;
  /** Nom du dialogue. */
  label: string;
  /** `fullscreen` : porté sous `<body>`, modal ; `inline` : dans le cadre du parent (bibliothèque PhotoGrid), non modal. */
  presentation: "fullscreen" | "inline";
  chrome: ViewerChrome;
  /** Grossissement maximal. */
  zoom?: number;
  /** Rectangle de la cellule d'origine (vol d'ouverture). */
  originRect: () => DOMRect | null;
  /** Rectangle de la cellule de la photo COURANTE (vol de retour). */
  returnRect: (index: number) => DOMRect | null;
  /** Rend le focus à la cellule de la photo courante à la fermeture (rappel canon `onCloseAutoFocus`). */
  focusReturn: (index: number) => void;
  /** Bibliothèque : favori de la photo courante. */
  favorite?: { is: (key: string) => boolean; toggle: (key: string) => void };
  /** Bibliothèque : ligne de détail sous le titre (date…). */
  detail?: (item: ViewerItem) => ReactNode;
  labels?: Partial<ViewerLabels>;
}

const clamp = (v: number, min: number, max: number) => Math.min(max, Math.max(min, v));
const fit = (w: number, h: number, maxW: number, maxH: number) => {
  const s = Math.min(maxW / w, maxH / h);
  return { width: Math.max(1, w * s), height: Math.max(1, h * s) };
};

/** Le viewer : Root du moteur commun toujours monté ; la surface entre et sort avec ses vols (AnimatePresence + usePresence). */
export function MediaViewer(props: MediaViewerProps) {
  const { open, onClose, presentation } = props;
  return (
    <Root open={open} onOpenChange={(next) => { if (!next) onClose(); }} modal={presentation === "fullscreen"}>
      {presentation === "fullscreen" ? (
        <AnimatePresence>{open && <Portal key="viewer" forceMount><Surface {...props} /></Portal>}</AnimatePresence>
      ) : (
        <AnimatePresence>{open && <Surface key="viewer" {...props} />}</AnimatePresence>
      )}
    </Root>
  );
}

type Gesture = {
  pointers: Map<number, { x: number; y: number }>;
  start: { x: number; y: number; t: number } | null;
  axis: "x" | "y" | "pan" | "pinch" | null;
  pinch: { distance: number; scale: number; cx: number; cy: number; px: number; py: number } | null;
  pan: { px: number; py: number } | null;
  samples: [number, number, number][];
  moved: boolean;
  /** Dernier engagement de l'hôte et présence de la surface : seuls juges d'un rappel différé (fin de ressort). */
  live: { engaged: boolean; present: boolean };
  /** Ressort de balayage en vol vers la photo voisine (arrêté au désengagement, à la fermeture et au démontage). */
  settle: { stop: () => void } | null;
};

function Surface({ onClose, items, index, onIndexChange, label, presentation, chrome, zoom: maxZoom = chrome === "library" ? 3 : 2.4, originRect, returnRect, focusReturn, favorite, detail, labels: overrides }: MediaViewerProps) {
  const t = useMemo(() => ({ ...viewerDefaultLabels, ...overrides }), [overrides]);
  const reduced = useReducedMotion() ?? false;
  const engaged = useOverlayHostEngaged();
  const [isPresent, safeToRemove] = usePresence();
  const count = items.length;
  const item = items[index] ?? items[0];
  const stageRef = useRef<HTMLDivElement>(null);
  const [stage, setStage] = useState<{ width: number; height: number; left: number; top: number } | null>(null);
  const [zoomed, setZoomed] = useState(false);
  const [announcement, setAnnouncement] = useState(() => t.counter(index, count));
  // Mouvement : décalage de la bande (balayage), descente (fermeture), vol (x, y, échelle), zoom et panoramique de la photo courante.
  const strip = useMotionValue(0);
  const drop = useMotionValue(0);
  const flightX = useMotionValue(0), flightY = useMotionValue(0), flightS = useMotionValue(1);
  const fade = useMotionValue(reduced ? 0 : 1);
  const scale = useMotionValue(1), panX = useMotionValue(0), panY = useMotionValue(0);
  const backdrop = useTransform(drop, (d) => clamp(1 - Math.abs(d) / 420, 0, 1));
  const backdropOpacity = useTransform([fade, backdrop] as MotionValue<number>[], ([a, b]: number[]) => a! * b!);
  const g = useRef<Gesture>({ pointers: new Map(), start: null, axis: null, pinch: null, pan: null, samples: [], moved: false, live: { engaged, present: isPresent }, settle: null });
  const lastTap = useRef(0);
  const opened = useRef(false);

  const pad = presentation === "fullscreen" ? (stage && stage.width < 640 ? 12 : 48) : 16;
  const frameOf = useCallback((it: ViewerItem | undefined) => (it && stage ? fit(it.width, it.height, Math.max(1, stage.width - pad * 2), Math.max(1, stage.height - pad * 2)) : { width: 0, height: 0 }), [stage, pad]);
  const frame = frameOf(item);

  // Taille de la scène : ResizeObserver de l'élément (aucun écouteur de fenêtre).
  useLayoutEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const read = () => { const r = node.getBoundingClientRect(); setStage({ width: r.width, height: r.height, left: r.left, top: r.top }); };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(read);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  // Vol d'ouverture : la photo part du rectangle de sa cellule et se pose dans la scène (mouvement réduit : fondu).
  useLayoutEffect(() => {
    if (opened.current || !stage || !frame.width) return;
    opened.current = true;
    if (reduced) { animate(fade, 1, { duration: motionTokens.duration.fast }); return; }
    const from = originRect();
    if (!from) return;
    const cx = stage.left + stage.width / 2, cy = stage.top + stage.height / 2;
    flightX.jump(from.left + from.width / 2 - cx);
    flightY.jump(from.top + from.height / 2 - cy);
    flightS.jump(Math.max(from.width / frame.width, from.height / frame.height));
    fade.jump(0);
    animate(fade, 1, { duration: motionTokens.duration.fast });
    animate(flightX, 0, motionTokens.spring.smooth);
    animate(flightY, 0, motionTokens.spring.smooth);
    animate(flightS, 1, motionTokens.spring.smooth);
  }, [stage, frame.width, reduced, originRect, fade, flightX, flightY, flightS]);

  // Vol de retour : vers la cellule de la photo COURANTE, puis démontage.
  useEffect(() => {
    if (isPresent) return;
    abandonGesture();
    const done = () => safeToRemove?.();
    const to = returnRect(index);
    if (reduced || !to || !stage || !frame.width) { animate(fade, 0, { duration: motionTokens.duration.fast, onComplete: done }); return; }
    const cx = stage.left + stage.width / 2, cy = stage.top + stage.height / 2;
    animate(scale, 1, motionTokens.spring.smooth);
    animate(panX, 0, motionTokens.spring.smooth);
    animate(panY, 0, motionTokens.spring.smooth);
    animate(drop, 0, motionTokens.spring.smooth);
    animate(fade, 0, { duration: motionTokens.duration.standard, delay: 0.12 });
    animate(flightX, to.left + to.width / 2 - cx, motionTokens.spring.smooth);
    animate(flightY, to.top + to.height / 2 - cy - drop.get(), motionTokens.spring.smooth);
    animate(flightS, Math.max(to.width / frame.width, to.height / frame.height), { ...motionTokens.spring.smooth, onComplete: done });
    // Le vol de retour se joue une fois, à la sortie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPresent]);

  // Chaque changement de photo remet le zoom à plat et s'annonce.
  useEffect(() => {
    scale.jump(1); panX.jump(0); panY.jump(0); setZoomed(false);
    setAnnouncement(t.counter(index, count));
  }, [index, count, t, scale, panX, panY]);

  const zoomTo = useCallback((target: number, qx?: number, qy?: number) => {
    if (!stage) return;
    const s1 = scale.get(), s2 = clamp(target, 1, maxZoom);
    const cx = stage.left + stage.width / 2, cy = stage.top + stage.height / 2;
    const px = qx === undefined ? 0 : qx - cx, py = qy === undefined ? 0 : qy - cy;
    // Garde le point sous le pointeur fixe : p2 = q − c − s2·u avec u = (q − c − p1) / s1.
    const ux = (px - panX.get()) / s1, uy = (py - panY.get()) / s1;
    const limit = (s: number, size: number) => ((s - 1) * size) / 2;
    const nx = clamp(px - s2 * ux, -limit(s2, frame.width), limit(s2, frame.width));
    const ny = clamp(py - s2 * uy, -limit(s2, frame.height), limit(s2, frame.height));
    const run = reduced ? { duration: 0 } : motionTokens.spring.snappy;
    animate(scale, s2, run); animate(panX, s2 === 1 ? 0 : nx, run); animate(panY, s2 === 1 ? 0 : ny, run);
    setZoomed(s2 > 1.01);
  }, [stage, scale, panX, panY, maxZoom, frame.width, frame.height, reduced]);

  const go = useCallback((to: number) => {
    const next = clamp(to, 0, count - 1);
    if (next === index || !engaged) return;
    onIndexChange(next);
  }, [count, index, onIndexChange, engaged]);

  // Ctrl+molette (et pincement de pavé tactile) zoome autour du pointeur : écouteur de la SCÈNE, non passif.
  useEffect(() => {
    const node = stageRef.current;
    if (!node) return;
    const onWheel = (event: WheelEvent) => {
      if (!event.ctrlKey) return;
      event.preventDefault();
      zoomTo(scale.get() * Math.exp(-event.deltaY * 0.01), event.clientX, event.clientY);
    };
    node.addEventListener("wheel", onWheel, { passive: false });
    return () => node.removeEventListener("wheel", onWheel);
  }, [zoomTo, scale]);

  /**
   * Abandon du geste en cours ou de son ressort de balayage (hôte désengagé, fermeture, démontage) : la scène revient à l'état de
   * départ du geste, instantanément, sans navigation ni fermeture. L'ouverture, l'indice et le zoom déjà posés sont conservés.
   */
  const abandonGesture = () => {
    const s = g.current;
    s.settle?.stop();
    s.settle = null;
    strip.jump(0);
    if (s.axis === "y") drop.jump(0);
    else if (s.axis === "pan" && s.pan) { panX.jump(s.pan.px); panY.jump(s.pan.py); }
    else if (s.axis === "pinch" && s.pinch) { scale.jump(s.pinch.scale); panX.jump(s.pinch.px); panY.jump(s.pinch.py); setZoomed(s.pinch.scale > 1.01); }
    s.pointers.clear(); s.axis = null; s.pinch = null; s.start = null;
  };
  useLayoutEffect(() => {
    g.current.live = { engaged, present: isPresent };
    if (!engaged) abandonGesture();
    // abandonGesture ne lit que des refs et des valeurs de mouvement stables.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engaged, isPresent]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => { g.current.settle?.stop(); g.current.settle = null; }, []);

  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!engaged || (event.pointerType === "mouse" && event.button !== 0)) return;
    const s = g.current;
    s.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    event.currentTarget.setPointerCapture?.(event.pointerId);
    if (s.pointers.size === 2) {
      const [a, b] = [...s.pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
      s.axis = "pinch";
      s.pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: scale.get(), cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2, px: panX.get(), py: panY.get() };
      return;
    }
    s.start = { x: event.clientX, y: event.clientY, t: event.timeStamp };
    s.axis = scale.get() > 1.01 ? "pan" : null;
    s.pan = { px: panX.get(), py: panY.get() };
    s.samples = [[event.timeStamp, event.clientX, event.clientY]];
    s.moved = false;
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const s = g.current;
    // Produit : zoomé, la photo suit le pointeur souris sans appui (« pans after the pointer »).
    if (chrome === "product" && event.pointerType === "mouse" && !s.start && zoomed && stage) {
      const cx = stage.left + stage.width / 2, cy = stage.top + stage.height / 2, z = scale.get();
      const lx = ((z - 1) * frame.width) / 2, ly = ((z - 1) * frame.height) / 2;
      panX.set(clamp((-(event.clientX - cx) / (frame.width / 2)) * lx, -lx, lx));
      panY.set(clamp((-(event.clientY - cy) / (frame.height / 2)) * ly, -ly, ly));
      return;
    }
    if (!s.pointers.has(event.pointerId)) return;
    s.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (s.axis === "pinch" && s.pinch && s.pointers.size >= 2) {
      const [a, b] = [...s.pointers.values()] as [{ x: number; y: number }, { x: number; y: number }];
      const ratio = Math.hypot(a.x - b.x, a.y - b.y) / Math.max(1, s.pinch.distance);
      const target = clamp(s.pinch.scale * ratio, 1, maxZoom);
      scale.set(target);
      // Le point de la photo pris sous le milieu initial des doigts suit leur milieu courant (zoom et translation), dans les
      // bornes de la photo : u = (m0 − c − p0) / s0, puis p = m − c − s·u (même relation que zoomTo).
      if (stage) {
        const c = { x: stage.left + stage.width / 2, y: stage.top + stage.height / 2 };
        const ux = (s.pinch.cx - c.x - s.pinch.px) / s.pinch.scale, uy = (s.pinch.cy - c.y - s.pinch.py) / s.pinch.scale;
        const lx = ((target - 1) * frame.width) / 2, ly = ((target - 1) * frame.height) / 2;
        panX.set(clamp((a.x + b.x) / 2 - c.x - target * ux, -lx, lx));
        panY.set(clamp((a.y + b.y) / 2 - c.y - target * uy, -ly, ly));
      }
      setZoomed(target > 1.01);
      return;
    }
    if (!s.start) return;
    const dx = event.clientX - s.start.x, dy = event.clientY - s.start.y;
    s.samples.push([event.timeStamp, event.clientX, event.clientY]);
    if (s.samples.length > 12) s.samples.shift();
    if (!s.axis) {
      if (Math.hypot(dx, dy) < 6) return;
      s.axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
    }
    s.moved = true;
    if (s.axis === "pan" && s.pan) {
      const z = scale.get(), lx = ((z - 1) * frame.width) / 2, ly = ((z - 1) * frame.height) / 2;
      panX.set(clamp(s.pan.px + dx, -lx, lx));
      panY.set(clamp(s.pan.py + dy, -ly, ly));
    } else if (s.axis === "x") {
      // Bords : résistance élastique quand il n'y a plus de photo de ce côté.
      const edge = (dx > 0 && index === 0) || (dx < 0 && index === count - 1);
      strip.set(edge ? dx * 0.3 : dx);
    } else if (s.axis === "y") {
      drop.set(dy > 0 ? dy : dy * 0.25);
    }
  };
  /** Annulation (système, perte du pointeur) : le geste revient à son état de départ, sans navigation ni fermeture. */
  const onPointerCancel = (event: ReactPointerEvent<HTMLDivElement>) => {
    const s = g.current;
    s.pointers.delete(event.pointerId);
    const run = reduced ? { duration: 0 } : motionTokens.spring.smooth;
    if (s.axis === "x") animate(strip, 0, run);
    else if (s.axis === "y") animate(drop, 0, run);
    else if (s.axis === "pan" && s.pan) { animate(panX, s.pan.px, run); animate(panY, s.pan.py, run); }
    else if (s.axis === "pinch" && s.pinch && s.pointers.size === 0) { animate(scale, s.pinch.scale, run); animate(panX, s.pinch.px, run); animate(panY, s.pinch.py, run); setZoomed(s.pinch.scale > 1.01); }
    if (s.pointers.size === 0) { s.axis = null; s.pinch = null; }
    s.start = null;
  };
  const onPointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    // Hôte désengagé pendant le geste : rien n'est validé (aucune navigation ni fermeture cachée).
    if (!engaged) { onPointerCancel(event); return; }
    const s = g.current;
    s.pointers.delete(event.pointerId);
    if (s.axis === "pinch") {
      if (s.pointers.size === 0) { s.axis = null; s.pinch = null; if (scale.get() < 1.05) zoomTo(1); }
      return;
    }
    const start = s.start;
    s.start = null;
    if (!start) return;
    const recent = s.samples.filter(([time]) => event.timeStamp - time < 90);
    const first = recent[0], last = recent[recent.length - 1];
    const dt = first && last && last[0] > first[0] ? (last[0] - first[0]) / 1000 : 0;
    const vx = dt ? ((last![1] - first![1]) / dt) : 0, vy = dt ? ((last![2] - first![2]) / dt) : 0;
    const width = stage?.width ?? 1;
    if (s.axis === "x") {
      const offset = strip.get();
      const dir = offset < -width * 0.18 || vx < -500 ? 1 : offset > width * 0.18 || vx > 500 ? -1 : 0;
      const next = clamp(index + dir, 0, count - 1);
      if (next !== index && !reduced) {
        // Le rappel de fin juge sur le DERNIER engagement et la présence (refs), pas sur la fermeture du rendu du relâcher.
        s.settle = animate(strip, -dir * width, { ...motionTokens.spring.smooth, velocity: vx, onComplete: () => {
          s.settle = null;
          strip.jump(0);
          if (s.live.engaged && s.live.present) go(next);
        } });
      } else {
        if (next !== index) go(next);
        if (reduced) strip.jump(0); else animate(strip, 0, motionTokens.spring.smooth);
      }
    } else if (s.axis === "y") {
      if (drop.get() > 120 || vy > 700) onClose();
      else if (reduced) drop.jump(0); else animate(drop, 0, motionTokens.spring.smooth);
    } else if (!s.moved) {
      // Tap / clic : produit = bascule de zoom ; ailleurs, double tap = zoom autour du point.
      if (chrome === "product") zoomTo(zoomed ? 1 : maxZoom, event.clientX, event.clientY);
      else {
        const now = event.timeStamp;
        if (now - lastTap.current < 300) { zoomTo(zoomed ? 1 : maxZoom, event.clientX, event.clientY); lastTap.current = 0; }
        else lastTap.current = now;
      }
    }
    s.axis = null;
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!engaged || event.altKey || event.metaKey || event.ctrlKey) return;
    const target = event.target as HTMLElement;
    if (target.getAttribute("role") === "slider") return;
    const k = event.key;
    if (k === "ArrowLeft") go(index - 1);
    else if (k === "ArrowRight") go(index + 1);
    else if (k === "Home" && chrome === "library") go(0);
    else if (k === "End" && chrome === "library") go(count - 1);
    else if (k === "+" || k === "=") zoomTo(maxZoom);
    else if (k === "-") zoomTo(1);
    else if (k === "0") zoomTo(1);
    else return;
    event.preventDefault();
  };

  const fullscreen = presentation === "fullscreen";
  const current = items[index];
  const titleText = current?.title ?? current?.alt ?? "";
  const surfaceStyle = { opacity: fade } as unknown as CSSProperties;
  const content = (
    <Content
      asChild
      forceMount
      onOpenAutoFocus={(event) => {
        // Focus d'entrée sur Fermer (ou Retour en bibliothèque), comme la documentation publique.
        event.preventDefault();
        (stageRef.current?.closest("[role=dialog]")?.querySelector("[data-viewer-initial]") as HTMLElement | null)?.focus();
      }}
      onCloseAutoFocus={(event) => {
        // Le focus revient à la cellule de la photo COURANTE (pas forcément celle d'origine).
        event.preventDefault();
        focusReturn(index);
      }}
      onEscapeKeyDown={(event) => {
        // Produit (contrat public) : Échap dézoome d'abord, puis ferme. Lightbox et bibliothèque : Échap ferme directement.
        // L'état `zoomed` (posé au geste) fait foi, pas la valeur animée qui peut encore être en route.
        if (chrome === "product" && zoomed) { event.preventDefault(); zoomTo(1); }
      }}
    >
      <motion.div className={styles.surface} data-presentation={presentation} data-chrome={chrome} style={surfaceStyle} onKeyDown={onKeyDown}>
        <Title className={styles.srOnly}>{chrome === "product" ? `${label}, ${t.photoOf(index, count)}` : label}</Title>
        <ViewerChromeBar chrome={chrome} t={t} index={index} count={count} zoomed={zoomed} current={current} detail={detail} favorite={favorite}
          onClose={onClose} onZoom={() => zoomTo(zoomed ? 1 : maxZoom)} />
        <div ref={stageRef} className={styles.stage} data-zoomed={zoomed ? "" : undefined} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerCancel}>
          {items.map((it, i) => (Math.abs(i - index) <= 1 ? (
            <Slide key={it.key} item={it} offset={i - index} current={i === index} frame={frameOf(it)} stageWidth={stage?.width ?? 0}
              strip={strip} drop={drop} flight={{ x: flightX, y: flightY, s: flightS }} zoom={{ s: scale, x: panX, y: panY }} eager={i === index} />
          ) : null))}
          {chrome !== "product" ? (
            <>
              <span className={styles.side} data-side="prev"><Button variant="secondary" size="md" shape="icon" aria-label={t.previous} disabled={index === 0} onClick={() => go(index - 1)}><ChevronLeft size={20} strokeWidth={1.75} aria-hidden="true" /></Button></span>
              <span className={styles.side} data-side="next"><Button variant="secondary" size="md" shape="icon" aria-label={t.next} disabled={index === count - 1} onClick={() => go(index + 1)}><ChevronRight size={20} strokeWidth={1.75} aria-hidden="true" /></Button></span>
            </>
          ) : null}
        </div>
        {chrome === "lightbox" ? (
          <div className={styles.footer}>
            {current?.title || current?.caption ? (
              <div className={styles.caption}>
                {current?.title ? <span className={styles.captionTitle}>{current.title}</span> : null}
                {current?.caption ? <span className={styles.captionLine}>{current.caption}</span> : null}
              </div>
            ) : null}
            <div className={styles.thumbs}>
              {items.map((it, i) => (
                <button key={it.key} type="button" className={styles.thumb} aria-label={t.show(it.title ?? it.alt)} aria-current={i === index ? "true" : undefined} onClick={() => go(i)}>
                  <img src={it.src} srcSet={it.srcSet} alt="" loading="lazy" decoding="async" draggable={false} />
                </button>
              ))}
            </div>
          </div>
        ) : chrome === "product" ? (
          <div className={styles.footer} data-chrome="product">
            <Button variant="secondary" size="md" shape="icon" aria-label={t.previous} disabled={index === 0} onClick={() => go(index - 1)}><ChevronLeft size={18} strokeWidth={1.75} aria-hidden="true" /></Button>
            <span className={styles.dots} aria-hidden="true">{items.map((it, i) => <span key={it.key} className={styles.dot} data-on={i === index ? "" : undefined} />)}</span>
            <Button variant="secondary" size="md" shape="icon" aria-label={t.next} disabled={index === count - 1} onClick={() => go(index + 1)}><ChevronRight size={18} strokeWidth={1.75} aria-hidden="true" /></Button>
          </div>
        ) : (
          <Filmstrip items={items} index={index} label={t.filmstrip} titleOf={(it) => it.title ?? it.alt} onChange={go} />
        )}
        <span className={styles.srOnly} role="status" aria-live="polite">{chrome === "library" ? `${titleText}, ${announcement}` : announcement}</span>
      </motion.div>
    </Content>
  );
  return fullscreen ? (
    <>
      <Overlay asChild forceMount>
        <motion.div className={styles.backdrop} style={{ opacity: backdropOpacity }} />
      </Overlay>
      {content}
    </>
  ) : content;
}

function ViewerChromeBar({ chrome, t, index, count, zoomed, current, detail, favorite, onClose, onZoom }: { chrome: ViewerChrome; t: ViewerLabels; index: number; count: number; zoomed: boolean; current: ViewerItem | undefined; detail?: (item: ViewerItem) => ReactNode; favorite?: MediaViewerProps["favorite"]; onClose: () => void; onZoom: () => void }) {
  if (chrome === "library") {
    const fav = current && favorite ? favorite.is(current.key) : false;
    return (
      <div className={styles.bar} data-chrome="library">
        <Button data-viewer-initial="" variant="secondary" size="md" shape="icon" aria-label={t.back} onClick={onClose}><ArrowLeft size={18} strokeWidth={1.75} aria-hidden="true" /></Button>
        <div className={styles.heading}>
          <span className={styles.headingTitle}>{current?.title ?? current?.alt}</span>
          <span className={styles.headingDetail}>{current && detail ? <>{detail(current)} · </> : null}{t.counter(index, count)}</span>
        </div>
        {favorite && current ? (
          <Button variant="secondary" size="md" shape="icon" aria-label={t.favorite} aria-pressed={fav} onClick={() => favorite.toggle(current.key)}>
            <AnimatePresence initial={false} mode="popLayout">
              <motion.span key={fav ? "on" : "off"} className={styles.heart} data-on={fav ? "" : undefined} initial={{ scale: 0.6, opacity: 0, filter: `blur(${motionTokens.blur.subtle}px)` }} animate={{ scale: 1, opacity: 1, filter: "blur(0px)" }} exit={{ scale: 0.6, opacity: 0 }} transition={motionTokens.spring.snappy}>
                <Heart size={18} strokeWidth={1.75} fill={fav ? "currentColor" : "none"} aria-hidden="true" />
              </motion.span>
            </AnimatePresence>
          </Button>
        ) : <span />}
      </div>
    );
  }
  return (
    <div className={styles.bar} data-chrome={chrome}>
      <span className={styles.counter}>{chrome === "product" ? `${index + 1} / ${count}` : t.counter(index, count)}</span>
      {chrome === "product" ? <span className={styles.hint}>{t.hint}</span> : <span />}
      <span className={styles.barEnd}>
        {chrome === "lightbox" ? (
          <Button variant="ghost" size="md" shape="icon" aria-label={zoomed ? t.zoomOut : t.zoomIn} aria-pressed={zoomed} onClick={onZoom}>
            {zoomed ? <ZoomOut size={20} strokeWidth={1.75} aria-hidden="true" /> : <ZoomIn size={20} strokeWidth={1.75} aria-hidden="true" />}
          </Button>
        ) : null}
        <Button data-viewer-initial="" variant={chrome === "product" ? "secondary" : "ghost"} size="md" shape="icon" aria-label={chrome === "product" ? t.closeShort : t.close} onClick={onClose}>
          <X size={20} strokeWidth={1.75} aria-hidden="true" />
        </Button>
      </span>
    </div>
  );
}

/** Une photo dans la bande : la courante porte le vol, le zoom et le panoramique ; les voisines suivent le balayage. */
const Slide = forwardRef<HTMLDivElement, { item: ViewerItem; offset: number; current: boolean; frame: { width: number; height: number }; stageWidth: number; strip: MotionValue<number>; drop: MotionValue<number>; flight: { x: MotionValue<number>; y: MotionValue<number>; s: MotionValue<number> }; zoom: { s: MotionValue<number>; x: MotionValue<number>; y: MotionValue<number> }; eager: boolean }>(
  function Slide({ item, offset, current, frame, stageWidth, strip, drop, flight, zoom, eager }, ref) {
    const x = useTransform(strip, (v) => v + offset * (stageWidth + 24));
    const layerY = useTransform(drop, (v) => (current ? v : 0));
    const dropScale = useTransform(drop, (v) => (current ? 1 - clamp(Math.abs(v) / 2400, 0, 0.18) : 1));
    const fx = useTransform([flight.x, zoom.x] as MotionValue<number>[], ([a, b]: number[]) => (current ? a! + b! : 0));
    const fy = useTransform([flight.y, zoom.y] as MotionValue<number>[], ([a, b]: number[]) => (current ? a! + b! : 0));
    const fs = useTransform([flight.s, zoom.s, dropScale] as MotionValue<number>[], ([a, b, c]: number[]) => (current ? a! * b! * c! : 1));
    return (
      <motion.div ref={ref} className={styles.layer} style={{ x, y: layerY }} aria-hidden={current ? undefined : true} data-current={current ? "" : undefined}>
        <motion.div className={styles.frame} style={{ width: frame.width, height: frame.height, x: fx, y: fy, scale: fs }}>
          <img src={item.src} srcSet={item.srcSet} alt={current ? item.alt : ""} loading={eager ? "eager" : "lazy"} decoding="async" draggable={false} className={styles.image} />
        </motion.div>
      </motion.div>
    );
  },
);

/** Bibliothèque : filmstrip = curseur (role slider) dont la valeur est la photo, titre en aria-valuetext ; glisser pour parcourir. */
function Filmstrip({ items, index, label, titleOf, onChange }: { items: ViewerItem[]; index: number; label: string; titleOf: (it: ViewerItem) => string; onChange: (i: number) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const scrub = useRef<number | null>(null);
  const pick = (clientX: number) => {
    const node = ref.current;
    if (!node) return;
    const cells = [...node.querySelectorAll<HTMLElement>("[data-film]")];
    const hit = cells.findIndex((c) => { const r = c.getBoundingClientRect(); return clientX >= r.left && clientX <= r.right; });
    if (hit >= 0) onChange(hit);
  };
  return (
    <div
      ref={ref}
      className={styles.film}
      role="slider"
      tabIndex={0}
      aria-label={label}
      aria-valuemin={1}
      aria-valuemax={items.length}
      aria-valuenow={index + 1}
      aria-valuetext={items[index] ? titleOf(items[index]!) : undefined}
      onKeyDown={(event) => {
        const k = event.key;
        const to = k === "ArrowLeft" || k === "ArrowDown" ? index - 1 : k === "ArrowRight" || k === "ArrowUp" ? index + 1 : k === "Home" ? 0 : k === "End" ? items.length - 1 : k === "PageUp" ? index - 5 : k === "PageDown" ? index + 5 : null;
        if (to === null) return;
        event.preventDefault();
        event.stopPropagation();
        onChange(clamp(to, 0, items.length - 1));
      }}
      onPointerDown={(event) => { scrub.current = event.pointerId; event.currentTarget.setPointerCapture?.(event.pointerId); pick(event.clientX); }}
      onPointerMove={(event) => { if (scrub.current === event.pointerId) pick(event.clientX); }}
      onPointerUp={() => { scrub.current = null; }}
      onPointerCancel={() => { scrub.current = null; }}
    >
      {items.map((it, i) => (
        <span key={it.key} data-film="" className={styles.filmCell} data-current={i === index ? "" : undefined}>
          <img src={it.src} srcSet={it.srcSet} alt="" loading="lazy" decoding="async" draggable={false} />
        </span>
      ))}
    </div>
  );
}

export default MediaViewer;
